import React from "react";
import { NodeID } from "@hex-insights/app-modules";
import {
	Button,
	ClassNameProps,
	Column,
	compareNumbersAsc,
	compareObjects,
	Conditional,
	Else,
	filterAndSortByQuery,
	filterMap,
	getArrayDiff,
	Grid,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	StyleProps,
	toGrammaticalNumber,
	TruncatedText,
	ViewableObserver,
} from "@hex-insights/core";
import { SlimEmptyView } from "../../../../../Components/Empty";
import { HorizontalScrollingContainer } from "../../../../../Components/HorizontalScrollingContainer";
import { Icon } from "../../../../../Components/Icon";
import { SearchField } from "../../../../../Components/Search";
import {
	truncateDisplayItems,
	User,
	UserGroup,
	UserGroupNameAndColorFragment,
	useSearchFormState,
} from "../../../../../Utilities";
import { UserFormat, UserGroupFormat } from "../../../../Format";
import * as UserComponents from "../../../User";
import * as UserGroupComponents from "../../../UserGroup";
import { MessagingUser } from "./utils";
import styles from "./styles.module.css";

const itemsPerRow = 20;
const modalPageSize = 30;

const messagingRecipientButtonWidth = "8rem";

type MessagingUserGroup = Pick<UserGroup, "id"> &
	UserGroupNameAndColorFragment & {
		members: Pick<User, "id" | "isCurrent">[];
	};

const defaultSelectedUserIDs: User["id"][] = [];
const defaultSelectedUserGroupIDs: UserGroup["id"][] = [];

export type UserRecipientSectionProps = {
	name: string;
	users: MessagingUser[];
	onClickRecipient: (id: User["id"]) => void;
	selectedUserIDs?: User["id"][];
} & Partial<ClassNameProps & StyleProps>;

export function UserRecipientSection({
	name,
	users,
	onClickRecipient,
	selectedUserIDs = defaultSelectedUserIDs,
	className,
	style,
}: UserRecipientSectionProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { displayedItems, anyHidden, numHidden } = truncateDisplayItems(users, itemsPerRow);

	return (
		<Column justify="spaced-start" className={className} style={style}>
			<Row justify="space-between" align="center">
				<Heading.H4 noMargin>{name}</Heading.H4>

				<Button variant="link" size="small" onClick={openModal}>
					View All
				</Button>
			</Row>

			<HorizontalScrollingContainer>
				<Row justify="spaced-start">
					{displayedItems.map((user) => (
						<UserRecipientButton
							key={user.id}
							user={user}
							onClick={onClickRecipient}
							isSelected={selectedUserIDs.includes(user.id)}
						/>
					))}
					<If condition={anyHidden}>
						<ViewAllRecipientsButton count={numHidden} onClick={openModal} />
					</If>
				</Row>
			</HorizontalScrollingContainer>

			<Modal.If condition={isModalOpen}>
				<UserRecipientModal
					name={name}
					users={users}
					onClickRecipient={onClickRecipient}
					selectedUserIDs={selectedUserIDs}
					onClose={closeModal}
				/>
			</Modal.If>
		</Column>
	);
}

type UserRecipientModalProps = UserRecipientSectionProps &
	Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function UserRecipientModal({
	name,
	users,
	onClickRecipient,
	selectedUserIDs = defaultSelectedUserIDs,
	ifRef,
	onClose,
	className,
	style,
}: UserRecipientModalProps) {
	const searchFormState = useSearchFormState();
	const { search } = searchFormState.formValues;
	const filteredUsers = React.useMemo(() => {
		return filterAndSortByQuery(search, users, (user) => UserFormat.name(user));
	}, [search, users]);

	const [numShown, setNumShown] = React.useState(modalPageSize);
	const incrementNumShown = React.useCallback(() => {
		setNumShown((prev) => prev + modalPageSize);
	}, []);
	React.useEffect(() => {
		setNumShown(modalPageSize);
	}, [search]);

	return (
		<Modal
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["recipients-modal"], className)}
			style={style}
		>
			<Modal.Header>
				<Heading.H2 noMargin>{name}</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<SearchField formState={searchFormState} name="search" />

					<Grid tracks="auto-fill" minTrackWidth={messagingRecipientButtonWidth}>
						{filteredUsers.slice(0, numShown).map((user) => (
							<UserRecipientButton
								key={user.id}
								user={user}
								onClick={onClickRecipient}
								isSelected={selectedUserIDs.includes(user.id)}
							/>
						))}
						<If condition={numShown < filteredUsers.length}>
							<ViewableObserver onEnterView={incrementNumShown} />
						</If>
					</Grid>
				</Column>
			</Modal.Body>
		</Modal>
	);
}

type UserRecipientButtonProps = {
	user: MessagingUser;
	isSelected?: boolean;
	onClick: (id: User["id"]) => void;
};

function UserRecipientButton({ user, isSelected = false, onClick }: UserRecipientButtonProps) {
	const description = user.organizationName;

	const fullOnClick = React.useCallback(() => {
		onClick(user.id);
	}, [onClick, user.id]);

	return (
		<React.Fragment>
			<Button
				onClick={fullOnClick}
				className={makeClassName(styles["recipient-button"], isSelected ? styles["recipient-button--selected"] : "")}
				style={{ width: messagingRecipientButtonWidth }}
			>
				<If condition={isSelected}>
					<div className={styles["recipient-button__x-button"]}>
						<Icon.X size={10} className="block" />
					</div>
				</If>
				<Column justify="spaced-start" align="center" verticalSpacing="0.375rem">
					<UserComponents.UserIcon user={user} size={46} />
					<Column align="center" style={{ width: "100%", overflow: "hidden" }}>
						<TruncatedText className={styles["recipient-button__name"]}>{UserFormat.name(user)}</TruncatedText>
						<TruncatedText className={styles["recipient-button__description"]}>{description}</TruncatedText>
					</Column>
				</Column>
			</Button>
		</React.Fragment>
	);
}

export type UserGroupRecipientSectionProps = {
	name: string;
	userGroups: MessagingUserGroup[];
	onClickRecipient: (id: UserGroup["id"]) => void;
	selectedUserGroupIDs?: UserGroup["id"][];
} & Partial<ClassNameProps & StyleProps>;

export function UserGroupRecipientSection({
	name,
	userGroups,
	onClickRecipient,
	selectedUserGroupIDs = defaultSelectedUserGroupIDs,
	className,
	style,
}: UserGroupRecipientSectionProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { displayedItems, anyHidden, numHidden } = truncateDisplayItems(userGroups, itemsPerRow);

	return (
		<Column justify="spaced-start" className={className} style={style}>
			<Row justify="space-between" align="center">
				<Heading.H4 noMargin>{name}</Heading.H4>

				<Button variant="link" size="small" onClick={openModal}>
					View All
				</Button>
			</Row>

			<HorizontalScrollingContainer>
				<Row justify="spaced-start">
					{displayedItems.map((userGroup) => (
						<UserGroupRecipientButton
							key={userGroup.id}
							userGroup={userGroup}
							onClick={onClickRecipient}
							isSelected={selectedUserGroupIDs.includes(userGroup.id)}
						/>
					))}
					<If condition={anyHidden}>
						<ViewAllRecipientsButton count={numHidden} onClick={openModal} />
					</If>
				</Row>
			</HorizontalScrollingContainer>

			<Modal.If condition={isModalOpen}>
				<UserGroupRecipientModal
					name={name}
					userGroups={userGroups}
					onClickRecipient={onClickRecipient}
					selectedUserGroupIDs={selectedUserGroupIDs}
					onClose={closeModal}
				/>
			</Modal.If>
		</Column>
	);
}

type UserGroupRecipientModalProps = UserGroupRecipientSectionProps &
	Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function UserGroupRecipientModal({
	name,
	userGroups,
	onClickRecipient,
	selectedUserGroupIDs = defaultSelectedUserGroupIDs,
	ifRef,
	onClose,
	className,
	style,
}: UserGroupRecipientModalProps) {
	const searchFormState = useSearchFormState();
	const { search } = searchFormState.formValues;
	const filteredUserGroups = React.useMemo(() => {
		return filterAndSortByQuery(search, userGroups, (userGroup) => UserGroupFormat.name(userGroup));
	}, [search, userGroups]);

	const [numShown, setNumShown] = React.useState(modalPageSize);
	const incrementNumShown = React.useCallback(() => {
		setNumShown((prev) => prev + modalPageSize);
	}, []);
	React.useEffect(() => {
		setNumShown(modalPageSize);
	}, [search]);

	return (
		<Modal
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["recipients-modal"], className)}
			style={style}
		>
			<Modal.Header>
				<Heading.H2 noMargin>{name}</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<SearchField formState={searchFormState} name="search" />

					<Grid tracks="auto-fill" minTrackWidth={messagingRecipientButtonWidth}>
						{filteredUserGroups.slice(0, numShown).map((userGroup) => (
							<UserGroupRecipientButton
								key={userGroup.id}
								userGroup={userGroup}
								onClick={onClickRecipient}
								isSelected={selectedUserGroupIDs.includes(userGroup.id)}
							/>
						))}
						<If condition={numShown < filteredUserGroups.length}>
							<ViewableObserver onEnterView={incrementNumShown} />
						</If>
					</Grid>
				</Column>
			</Modal.Body>
		</Modal>
	);
}

type UserGroupRecipientButtonProps = {
	userGroup: MessagingUserGroup;
	isSelected?: boolean;
	onClick: (id: UserGroup["id"]) => void;
};

function UserGroupRecipientButton({ userGroup, isSelected = false, onClick }: UserGroupRecipientButtonProps) {
	const fullOnClick = React.useCallback(() => {
		onClick(userGroup.id);
	}, [onClick, userGroup.id]);

	return (
		<React.Fragment>
			<Button
				onClick={fullOnClick}
				className={makeClassName(styles["recipient-button"], isSelected ? styles["recipient-button--selected"] : "")}
				style={{ width: messagingRecipientButtonWidth }}
			>
				<If condition={isSelected}>
					<div className={styles["recipient-button__x-button"]}>
						<Icon.X size={10} className="block" />
					</div>
				</If>
				<Column justify="spaced-start" align="center" verticalSpacing="0.375rem">
					<UserGroupComponents.UserGroupIcon userGroup={userGroup} size={46} />
					<Column align="center" style={{ width: "100%", overflow: "hidden" }}>
						<TruncatedText className={styles["recipient-button__name"]}>
							{UserGroupFormat.name(userGroup)}
						</TruncatedText>
						<TruncatedText className={styles["recipient-button__description"]}>
							{toGrammaticalNumber("Member", userGroup.members.length, true)}
						</TruncatedText>
					</Column>
				</Column>
			</Button>
		</React.Fragment>
	);
}

type ViewAllRecipientsButtonProps = {
	count: number;
	onClick: () => void;
};

function ViewAllRecipientsButton({ count, onClick }: ViewAllRecipientsButtonProps) {
	return (
		<Button onClick={onClick} className={styles["recipient-button"]} style={{ width: messagingRecipientButtonWidth }}>
			<Column justify="spaced-start" align="center" verticalSpacing="0.375rem">
				<UserComponents.UserAdditionalCountIcon count={count} size={46} />
				<Column align="center" style={{ width: "100%", overflow: "hidden" }}>
					<TruncatedText className={styles["recipient-button__name"]}>View All</TruncatedText>
					<TruncatedText className={styles["recipient-button__description"]}>{count} Others</TruncatedText>
				</Column>
			</Column>
		</Button>
	);
}

export type SelectedRecipientSectionProps = {
	users: MessagingUser[];
	onClickUserRecipient: (id: User["id"]) => void;
	selectedUserIDs?: User["id"][];
	userGroups: MessagingUserGroup[];
	onClickUserGroupRecipient: (id: UserGroup["id"]) => void;
	selectedUserGroupIDs?: UserGroup["id"][];
} & Partial<ClassNameProps & StyleProps>;

export function SelectRecipientSection({
	users,
	onClickUserRecipient,
	selectedUserIDs = defaultSelectedUserIDs,
	userGroups,
	onClickUserGroupRecipient,
	selectedUserGroupIDs = defaultSelectedUserGroupIDs,
	className,
	style,
}: SelectedRecipientSectionProps) {
	const [orderedRecipientIDs, setOrderedRecipientIDs] = React.useState(() => [
		...selectedUserIDs.map((id) => ({ id, typeName: "User" })),
		...selectedUserGroupIDs.map((id) => ({ id, typeName: "UserGroup" })),
	]);
	React.useEffect(() => {
		setOrderedRecipientIDs((prev) => {
			const prevUserIDs = filterMap(
				prev,
				(e) => e.typeName === "User",
				(e) => e.id,
			);
			const prevUserGroupIDs = filterMap(
				prev,
				(e) => e.typeName === "UserGroup",
				(e) => e.id,
			);

			const { added: addedUserIDs, removed: removedUserIDs } = getArrayDiff(prevUserIDs, selectedUserIDs);
			const { added: addedUserGroupIDs, removed: removedUserGroupIDs } = getArrayDiff(
				prevUserGroupIDs,
				selectedUserGroupIDs,
			);
			return [
				...addedUserIDs.map((id) => ({ id, typeName: "User" })),
				...addedUserGroupIDs.map((id) => ({ id, typeName: "UserGroup" })),
				...prev.filter((e) => !removedUserIDs.includes(e.id) && !removedUserGroupIDs.includes(e.id)),
			];
		});
	}, [selectedUserIDs, selectedUserGroupIDs]);

	const recipients = React.useMemo(() => {
		const selectedRecipientIDs = orderedRecipientIDs.map((r) => r.id);

		const selectedUsers = filterMap(
			users,
			(user) => selectedRecipientIDs.includes(user.id),
			(user) => ({ id: user.id, name: UserFormat.name(user), typeName: "User" }),
		);
		const selectedUserGroups = filterMap(
			userGroups,
			(userGroup) => selectedRecipientIDs.includes(userGroup.id),
			(userGroup) => ({ id: userGroup.id, name: UserGroupFormat.name(userGroup), typeName: "UserGroup" }),
		);

		return [...selectedUsers, ...selectedUserGroups].sort(
			compareObjects((e) => orderedRecipientIDs.findIndex((r) => r.id === e.id), compareNumbersAsc()),
		);
	}, [orderedRecipientIDs, users, userGroups]);

	return (
		<Column justify="spaced-start" className={className} style={style}>
			<Heading.H4 noMargin>
				Selected <If condition={recipients.length > 0}>({recipients.length})</If>
			</Heading.H4>

			<HorizontalScrollingContainer>
				<Row justify="spaced-start" style={{ flexGrow: 1, padding: "0.5rem 0" }}>
					<Conditional>
						<If condition={recipients.length > 0}>
							{recipients.map((recipient) => {
								let onClick = (_id: NodeID) => {};
								switch (recipient.typeName) {
									case "User":
										onClick = onClickUserRecipient;
										break;
									case "UserGroup":
										onClick = onClickUserGroupRecipient;
										break;
								}

								return (
									<SelectedRecipientButton
										key={recipient.id}
										id={recipient.id}
										name={recipient.name}
										onClick={onClick}
									/>
								);
							})}
						</If>
						<Else>
							<SlimEmptyView style={{ flexGrow: 1, height: "37px" }}>No recipients selected</SlimEmptyView>
						</Else>
					</Conditional>
				</Row>
			</HorizontalScrollingContainer>
		</Column>
	);
}

type SelectedRecipientButtonProps = {
	id: NodeID;
	name: string;
	onClick: (id: NodeID) => void;
};

function SelectedRecipientButton({ id, name, onClick }: SelectedRecipientButtonProps) {
	const fullOnClick = React.useCallback(() => {
		onClick(id);
	}, [onClick, id]);

	return (
		<Button variant="secondary" onClick={fullOnClick} className={styles["selected-recipient-button"]}>
			<Row justify="space-between" align="center">
				<TruncatedText className="nowrap">{name}</TruncatedText>

				<div className={styles["selected-recipient-button__x-button"]}>
					<Icon.X size={12} className="block" />
				</div>
			</Row>
		</Button>
	);
}
